/**
 * Web Search Service
 * Provides unified interface for web search, Wikipedia, and Discogs lookups
 * with Firestore caching for 24 hours
 */

import { db } from "@/lib/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  getDoc,
  doc,
  setDoc,
  Timestamp,
} from "firebase/firestore";

export interface CustomWebLink {
  id: string;
  title: string;
  url: string;
  snippet?: string;
  addedAt?: string;
}

export interface WebResult {
  title: string;
  url: string;
  snippet: string;
  source: string;
  favicon?: string;
  imageUrl?: string;
  publishedDate?: string;
  /** True when the returned result title doesn't closely match the requested entity name */
  lowConfidence?: boolean;
}

// ---------------------------------------------------------------------------
// Name-match confidence helpers
// ---------------------------------------------------------------------------

function normalizeName(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Returns true if the API result title is a confident match for entityName.
 * Requires an exact name match or one title containing the other as a phrase
 * (handles "Chris Twice (rapper)", "Chris Twice discography", etc.).
 * The old word-bag fallback is intentionally removed — it was too loose and
 * would match unrelated articles that happened to contain the same words.
 */
export function isConfidentMatch(resultTitle: string, entityName: string): boolean {
  const nResult = normalizeName(resultTitle);
  const nEntity = normalizeName(entityName);

  if (!nResult || !nEntity) return false;

  // Exact match (e.g. "Chris Twice" === "Chris Twice")
  if (nResult === nEntity) return true;

  // One fully contains the other as a substring/phrase
  // e.g. "chris twice rapper" contains "chris twice"
  // e.g. "chris twice" is contained in "chris twice discography"
  if (nResult.includes(nEntity) || nEntity.includes(nResult)) return true;

  return false;
}

interface CachedResult {
  query: string;
  type: string;
  results: WebResult[];
  timestamp: Timestamp;
}

const CACHE_DURATION_HOURS = 24;

/**
 * Check if cached result is still valid
 */
function isCacheValid(timestamp: Timestamp): boolean {
  const now = new Date();
  const cached = timestamp.toDate();
  const diffHours = (now.getTime() - cached.getTime()) / (1000 * 60 * 60);
  return diffHours < CACHE_DURATION_HOURS;
}

/**
 * Get cached results from Firestore
 */
async function getCachedResults(
  searchQuery: string,
  type: string = "all"
): Promise<WebResult[] | null> {
  try {
    const q = query(
      collection(db, "web_search_cache"),
      where("query", "==", searchQuery),
      where("type", "==", type)
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      const data = doc.data() as CachedResult;

      if (isCacheValid(data.timestamp)) {
        return data.results;
      }
    }
  } catch (err) {
    console.error("Error fetching cached results:", err);
  }

  return null;
}

/**
 * Cache results in Firestore
 */
async function cacheResults(
  searchQuery: string,
  type: string,
  results: WebResult[]
): Promise<void> {
  try {
    await addDoc(collection(db, "web_search_cache"), {
      query: searchQuery,
      type,
      results,
      timestamp: Timestamp.now(),
    });
  } catch (err) {
    console.error("Error caching results:", err);
  }
}

/**
 * Search the web using DuckDuckGo Instant Answer API (no key needed)
 * Falls back to suggesting Google search
 */
export async function searchWeb(
  query: string,
  type: string = "all"
): Promise<WebResult[]> {
  // Check cache first
  const cached = await getCachedResults(query, type);
  if (cached) {
    return cached;
  }

  // Try DuckDuckGo API
  try {
    const encodedQuery = encodeURIComponent(query);
    const ddgUrl = `https://api.duckduckgo.com/?q=${encodedQuery}&format=json`;

    const response = await fetch(ddgUrl);
    const data = await response.json();

    const results: WebResult[] = [];

    // Add abstract result if available
    if (data.Abstract && data.AbstractURL) {
      results.push({
        title: data.Heading || query,
        url: data.AbstractURL,
        snippet: data.Abstract,
        source: new URL(data.AbstractURL).hostname,
        favicon: `https://icons.duckduckgo.com/ip3/${new URL(data.AbstractURL).hostname}.ico`,
      });
    }

    // Add related topics
    if (data.RelatedTopics && data.RelatedTopics.length > 0) {
      const relatedResults = data.RelatedTopics.slice(0, 9).map(
        (item: any) => ({
          title: item.FirstURL?.split("/").pop() || item.Text,
          url: item.FirstURL || `https://duckduckgo.com/?q=${encodedQuery}`,
          snippet: item.Text,
          source: item.FirstURL
            ? new URL(item.FirstURL).hostname
            : "duckduckgo.com",
          favicon: item.FirstURL
            ? `https://icons.duckduckgo.com/ip3/${new URL(item.FirstURL).hostname}.ico`
            : undefined,
        })
      );
      results.push(...relatedResults);
    }

    // Cache results
    if (results.length > 0) {
      await cacheResults(query, type, results);
    }

    return results;
  } catch (err) {
    console.error("DuckDuckGo API error:", err);
  }

  // Fallback: return empty array with suggestion to search
  return [];
}

/**
 * Get Wikipedia excerpt for a query.
 * Pass entityName (the canonical display name) to enable confidence checking —
 * if the returned Wikipedia article title doesn't match, the result is flagged
 * as lowConfidence and is NOT cached so a future refresh can try again.
 */
export async function getWikipediaExcerpt(
  query: string,
  entityName?: string
): Promise<WebResult | null> {
  // Check cache first (only contains high-confidence results)
  const cached = await getCachedResults(query, "wikipedia");
  if (cached && cached.length > 0) {
    return cached[0];
  }

  try {
    const response = await fetch(
      `/api/wikipedia?action=search&q=${encodeURIComponent(query)}&limit=5`
    );
    const data = await response.json();

    if (data.results && data.results.length > 0) {
      // Try to find a confident match among the top results
      const matchName = entityName || query;
      let bestResult = data.results[0];
      let confident = false;

      for (const r of data.results) {
        if (isConfidentMatch(r.title, matchName)) {
          bestResult = r;
          confident = true;
          break;
        }
      }

      // Strip HTML tags from snippet (Wikipedia returns <span> highlights)
      const rawSnippet: string = bestResult.snippet || "";
      const cleanSnippet = rawSnippet.replace(/<[^>]+>/g, "");

      const wikiResult: WebResult = {
        title: bestResult.title,
        url: `https://en.wikipedia.org/wiki/${encodeURIComponent(bestResult.title)}`,
        snippet: cleanSnippet,
        source: "Wikipedia",
        favicon: "https://en.wikipedia.org/static/favicon/wikipedia.ico",
        lowConfidence: !confident,
      };

      // Only cache confident results so stale mismatches don't persist
      if (confident) {
        await cacheResults(query, "wikipedia", [wikiResult]);
      }
      return wikiResult;
    }
  } catch (err) {
    console.error("Wikipedia API error:", err);
  }

  return null;
}

/**
 * Get Discogs information about an artist/album.
 * Pass entityName to enable confidence checking — if the top result title
 * doesn't match the entity, up to 5 results are scanned for a better match.
 * Low-confidence results are returned but NOT cached.
 */
export async function getDiscogsInfo(
  query: string,
  type: "artist" | "album" = "artist",
  entityName?: string
): Promise<WebResult | null> {
  // Check cache (only contains high-confidence results)
  const cached = await getCachedResults(query, `discogs-${type}`);
  if (cached && cached.length > 0) {
    return cached[0];
  }

  try {
    const searchUrl = `https://api.discogs.com/database/search?q=${encodeURIComponent(query)}&type=${type}&per_page=5`;
    const response = await fetch(searchUrl, {
      headers: {
        "User-Agent": "BB-Native/1.0",
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    if (data.results && data.results.length > 0) {
      const matchName = entityName || query;
      let bestResult = data.results[0];
      let confident = false;

      for (const r of data.results) {
        if (isConfidentMatch(r.title, matchName)) {
          bestResult = r;
          confident = true;
          break;
        }
      }

      const discogsResult: WebResult = {
        title: bestResult.title,
        url: bestResult.uri || `https://www.discogs.com/search?q=${encodeURIComponent(query)}`,
        snippet: `${bestResult.type} on Discogs`,
        source: "Discogs",
        favicon: "https://www.discogs.com/favicon.ico",
        imageUrl: bestResult.thumb,
        lowConfidence: !confident,
      };

      // Only cache confident matches
      if (confident) {
        await cacheResults(query, `discogs-${type}`, [discogsResult]);
      }
      return discogsResult;
    }
  } catch (err) {
    console.error("Discogs API error:", err);
  }

  return null;
}

/**
 * Search for news about an entity using DuckDuckGo
 */
export async function searchNews(query: string): Promise<WebResult[]> {
  return searchWeb(`${query} news`);
}

/**
 * Get social media presence for an entity
 */
export async function getSearchLinks(
  entityName: string
): Promise<{
  google: string;
  wikipedia: string;
  discogs: string;
  spotify: string;
  bandcamp: string;
}> {
  const encoded = encodeURIComponent(entityName);
  return {
    google: `https://www.google.com/search?q=${encoded}`,
    wikipedia: `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encoded}&format=json`,
    discogs: `https://www.discogs.com/search?q=${encoded}`,
    spotify: `https://open.spotify.com/search/${encoded}`,
    bandcamp: `https://bandcamp.com/search?q=${encoded}`,
  };
}

/**
 * Load custom curated web links, persisted dismissals, AND any admin-set
 * query override from an entity document. Returns all three so the Web Info
 * panel can hide auto-scanned cards that were previously dismissed and use
 * the override search string when the auto-match is wrong.
 */
export async function getEntityWebLinks(
  entityCollection: string,
  entityId: string
): Promise<{ links: CustomWebLink[]; dismissed: string[]; approved: string[]; queryOverride: string }> {
  try {
    const snap = await getDoc(doc(db, entityCollection, entityId));
    if (snap.exists()) {
      const data = snap.data();
      return {
        links: (data?.webLinks as CustomWebLink[]) || [],
        dismissed: (data?.webLinksDismissed as string[]) || [],
        approved: (data?.webLinksApproved as string[]) || [],
        queryOverride: (data?.webInfoQueryOverride as string) || "",
      };
    }
  } catch (err) {
    console.error("Error loading entity web links:", err);
  }
  return { links: [], dismissed: [], approved: [], queryOverride: "" };
}

/**
 * Save custom curated web links (and optionally the dismissal list + query
 * override) to an entity document. Uses setDoc+merge so it works even when
 * the field doesn't yet exist, and returns false if the write fails.
 *
 * Passing queryOverride === "" clears the override (deleteField semantics
 * aren't worth the complexity here — empty string is the "no override" state).
 */
export async function saveEntityWebLinks(
  entityCollection: string,
  entityId: string,
  links: CustomWebLink[],
  dismissed?: string[],
  queryOverride?: string,
  approved?: string[],
): Promise<boolean> {
  try {
    const payload: Record<string, unknown> = { webLinks: links };
    if (dismissed !== undefined) payload.webLinksDismissed = dismissed;
    if (queryOverride !== undefined) payload.webInfoQueryOverride = queryOverride;
    if (approved !== undefined) payload.webLinksApproved = approved;
    await setDoc(doc(db, entityCollection, entityId), payload, { merge: true });
    return true;
  } catch (err) {
    console.error("Error saving entity web links:", err);
    return false;
  }
}
